import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

/**
 * Shared hook for wizard flow behavior
 * Manages question navigation, answer handling, and flow state
 * 
 * @param {Object} options
 * @param {Array<Object>} options.questions - Array of question definitions
 * @param {Object} options.answers - Current answers state
 * @param {function} options.setAnswers - Answer state setter
 * @param {function} options.onComplete - Called when flow is complete
 * @param {function} options.onBack - Called when back pressed at first question
 * @param {function} [options.shouldShowQuestion] - Function to filter visible questions
 * @param {boolean} [options.autoAdvanceOnRadio=false] - Auto-advance on radio selection
 * @param {number} [options.autoAdvanceDelay=300] - Delay before auto-advance (ms)
 * @param {number} [options.initialQuestionIndex=0] - Starting question index
 * @param {string} [options.initialQuestionId] - Starting question ID (takes precedence over index)
 * 
 * @returns {Object} Flow state and handlers
 */
export const useWizardFlow = ({
  questions,
  answers,
  setAnswers,
  onComplete,
  onBack,
  shouldShowQuestion = () => true,
  autoAdvanceOnRadio = false,
  autoAdvanceDelay = 300,
  initialQuestionIndex = 0,
  initialQuestionId = null
}) => {
  // Calculate initial index from ID if provided
  const computedInitialIndex = useMemo(() => {
    if (initialQuestionId) {
      const filteredQuestions = questions.filter(q => shouldShowQuestion(q, answers));
      const idx = filteredQuestions.findIndex(q => q.id === initialQuestionId);
      return idx >= 0 ? idx : initialQuestionIndex;
    }
    return initialQuestionIndex;
  }, [initialQuestionId, initialQuestionIndex, questions, shouldShowQuestion, answers]);
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(computedInitialIndex);
  const autoAdvanceTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);
  
  // Filter visible questions based on conditional logic
  const visibleQuestions = useMemo(() => {
    return questions.filter(q => shouldShowQuestion(q, answers));
  }, [questions, answers, shouldShowQuestion]);
  
  // Initialize to correct index on first render (handles initial ID lookup)
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Ensure index is within bounds
      if (computedInitialIndex >= 0 && computedInitialIndex < visibleQuestions.length) {
        setCurrentQuestionIndex(computedInitialIndex);
      } else if (visibleQuestions.length > 0) {
        setCurrentQuestionIndex(0);
      }
    }
  }, [computedInitialIndex, visibleQuestions.length]);
  
  // Ensure currentQuestionIndex stays within bounds when questions change
  useEffect(() => {
    if (currentQuestionIndex >= visibleQuestions.length && visibleQuestions.length > 0) {
      setCurrentQuestionIndex(visibleQuestions.length - 1);
    }
  }, [currentQuestionIndex, visibleQuestions.length]);
  
  // Current question and position info
  const currentQuestion = visibleQuestions[currentQuestionIndex];
  const currentQuestionId = currentQuestion?.id || null;
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === visibleQuestions.length - 1;
  const totalQuestions = visibleQuestions.length;
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;
  
  // Cleanup auto-advance timeout on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
    };
  }, []);
  
  // Check if current question has been answered
  const isCurrentAnswered = useMemo(() => {
    if (!currentQuestion) return false;
    
    const answer = answers[currentQuestion.id];
    
    switch (currentQuestion.type) {
      case 'text':
        return true; // Text fields are always optional
      case 'radio':
        return !!answer;
      case 'checkbox':
        return Array.isArray(answer) && answer.length > 0;
      case 'collapsible_categories':
        return Array.isArray(answer) && answer.length > 0;
      default:
        return !!answer;
    }
  }, [currentQuestion, answers]);
  
  // Can proceed to next question
  const canProceed = isCurrentAnswered;
  
  // Navigate to next question or complete
  const goNext = useCallback(() => {
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    
    if (isLastQuestion) {
      onComplete();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [isLastQuestion, onComplete]);
  
  // Navigate back
  const goBack = useCallback(() => {
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      onBack();
    }
  }, [currentQuestionIndex, onBack]);
  
  // Reset to initial question (uses initialQuestionIndex, NOT 0)
  const reset = useCallback(() => {
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    const targetIndex = Math.min(computedInitialIndex, visibleQuestions.length - 1);
    setCurrentQuestionIndex(Math.max(0, targetIndex));
  }, [computedInitialIndex, visibleQuestions.length]);
  
  // Reset to absolute first question (index 0)
  const resetToStart = useCallback(() => {
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    setCurrentQuestionIndex(0);
  }, []);
  
  // Go to specific question index
  const goToQuestion = useCallback((index) => {
    if (index >= 0 && index < visibleQuestions.length) {
      setCurrentQuestionIndex(index);
    }
  }, [visibleQuestions.length]);
  
  // Go to specific question by ID (stable across question insertions/removals)
  const goToQuestionById = useCallback((questionId) => {
    const idx = visibleQuestions.findIndex(q => q.id === questionId);
    if (idx >= 0) {
      setCurrentQuestionIndex(idx);
      return true;
    }
    return false;
  }, [visibleQuestions]);
  
  // Get current question index by ID (for saving/restoring position)
  const getQuestionIndexById = useCallback((questionId) => {
    return visibleQuestions.findIndex(q => q.id === questionId);
  }, [visibleQuestions]);
  
  // Handle radio answer with optional auto-advance
  const handleRadioAnswer = useCallback((value) => {
    if (!currentQuestion) return;
    
    // Clear any pending auto-advance first
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    
    // Auto-advance on radio selection (if enabled and not last question)
    if (autoAdvanceOnRadio && !isLastQuestion) {
      autoAdvanceTimeoutRef.current = setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, autoAdvanceDelay);
    }
  }, [currentQuestion, setAnswers, autoAdvanceOnRadio, isLastQuestion, autoAdvanceDelay]);
  
  // Handle checkbox answer (never auto-advances)
  const handleCheckboxChange = useCallback((optionValue, checked) => {
    if (!currentQuestion) return;
    
    const current = answers[currentQuestion.id] || [];
    const newValues = checked
      ? [...current, optionValue]
      : current.filter(v => v !== optionValue);
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: newValues }));
  }, [answers, currentQuestion, setAnswers]);
  
  // Handle text answer (never auto-advances)
  const handleTextAnswer = useCallback((value) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
  }, [currentQuestion, setAnswers]);
  
  // Handle generic answer update (for custom question types)
  const setAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, [setAnswers]);
  
  // Clear answer for a specific question
  const clearAnswer = useCallback((questionId) => {
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[questionId];
      return newAnswers;
    });
  }, [setAnswers]);
  
  // Check if a specific question is answered
  const isQuestionAnswered = useCallback((questionId, questionType = 'radio') => {
    const answer = answers[questionId];
    
    switch (questionType) {
      case 'text':
        return true;
      case 'checkbox':
      case 'collapsible_categories':
        return Array.isArray(answer) && answer.length > 0;
      default:
        return !!answer;
    }
  }, [answers]);
  
  // Get validation errors for current question
  const getValidationErrors = useCallback(() => {
    if (!currentQuestion) return [];
    
    const errors = [];
    
    if (!isCurrentAnswered && currentQuestion.type !== 'text') {
      errors.push({
        questionId: currentQuestion.id,
        message: `Please answer: ${currentQuestion.question}`
      });
    }
    
    return errors;
  }, [currentQuestion, isCurrentAnswered]);
  
  return {
    // Current state
    currentQuestion,
    currentQuestionId,
    currentQuestionIndex,
    visibleQuestions,
    totalQuestions,
    progress,
    isFirstQuestion,
    isLastQuestion,
    canProceed,
    isCurrentAnswered,
    
    // Navigation
    goNext,
    goBack,
    reset,
    resetToStart,
    goToQuestion,
    goToQuestionById,
    getQuestionIndexById,
    
    // Answer handlers
    handleRadioAnswer,
    handleCheckboxChange,
    handleTextAnswer,
    setAnswer,
    clearAnswer,
    
    // Validation
    isQuestionAnswered,
    getValidationErrors
  };
};

export default useWizardFlow;
